import {Search} from '@mui/icons-material';
import {debounce, OutlinedInput} from '@mui/material';
import {ChangeEvent, FC, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {AMPFormSelect} from 'src/components/common/AMPFormSelect';
import {FilterBar, FilterBarField} from 'src/components/common/FilterBar';
import {UserTypeEnum} from 'src/enums';
import {usePersona} from 'src/permissions/hooks';
import {searchInputSx} from 'src/components/tables/tableToolbar.sx';
import {UsersTableActionsType} from './UsersTableActions.types';

const ALL = '';

/**
 * The toolbar shared by the users list and the back-office team list.
 *
 * One filter, account type, and which types are on offer depends on the list and on the caller's
 * reach. Those entries are search-filter values, not permissions — they name kinds of person — but
 * only a platform-wide caller has other businesses' owners in range to filter by, and a property
 * manager has neither them nor the platform administrator.
 */
export const UsersTableActions: FC<UsersTableActionsType> = ({
                                                                 handleSearch,
                                                                 handleFilter,
                                                                 isManagersRoles,
                                                                 title,
                                                                 icon,
                                                                 onAdd,
                                                                 addLabel,
                                                                 totalElements,
                                                                 tabs,
                                                                 filters,
                                                             }) => {
    const intl = useIntl();
    const persona = usePersona();

    // Rendered from the filter the list is actually on, not from a copy of its own: the strip above
    // sets this same axis from its type tiles, and a select reading "all" over a narrowed list is
    // worse than no select at all.
    const role = filters.userTypes?.length === 1 ? String(filters.userTypes[0]) : ALL;

    const apply = (value: string) => handleFilter({role: value});

    const roleOptions = useMemo(() => {
        const all = isManagersRoles
            ? [UserTypeEnum.TECHNICAL_MANAGER, UserTypeEnum.SALES_MANAGER, UserTypeEnum.CLIENT_MANAGER]
            : [
                UserTypeEnum.BUSINESS,
                UserTypeEnum.PRIVATE_DRIVER,
                UserTypeEnum.SUPER_ADMIN,
                UserTypeEnum.COMPANY,
                UserTypeEnum.EMPLOYEE,
            ];
        const inReach = persona.isPlatformAdmin
            ? all
            : all
                .filter((type) => type !== UserTypeEnum.SUPER_ADMIN)
                .filter((type) => !persona.isRealEstate || type !== UserTypeEnum.BUSINESS);

        return [
            {id: 0, name: intl.formatMessage({id: 'allAccountTypes'}), value: ALL},
            ...inReach.map((type, index) => ({id: index + 1, name: type, value: type})),
        ];
    }, [isManagersRoles, persona.isPlatformAdmin, persona.isRealEstate, intl]);

    const fields: FilterBarField[] = [
        {
            id: 'accountType',
            wide: true,
            control: (
                <AMPFormSelect
                    dense
                    data={roleOptions}
                    value={role}
                    input="role"
                    onChange={(e) => apply(String(e.target.value))}
                />
            ),
        },
    ];

    return (
        <FilterBar
            title={title}
            icon={icon}
            onAdd={onAdd}
            addLabel={addLabel}
            subtitle={typeof totalElements === 'number'
                ? intl.formatMessage({id: 'rowsFound'}, {count: totalElements})
                : undefined}
            tabs={tabs}
            fields={fields}
            hasActiveFilters={Boolean(role)}
            onClearAll={() => apply(ALL)}
            actions={(
                <OutlinedInput
                    sx={searchInputSx}
                    color="secondary"
                    type="text"
                    placeholder={intl.formatMessage({id: 'search'})}
                    name="search"
                    // Uncontrolled for the debounce, but seeded: the box has to say what the list is
                    // filtered by after a Back, which remounts it with the keyword still in the URL.
                    defaultValue={filters?.keyword ?? ''}
                    onChange={(event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
                        debounce(() => handleSearch(event.target.value), 1000)()}
                    startAdornment={<Search/>}
                />
            )}
        />
    );
};
